/* eslint-disable react/prop-types, react-refresh/only-export-components */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiMapPin, FiMinus, FiPlus, FiShoppingBag, FiTrash2, FiX } from 'react-icons/fi';

export function formatCurrency(value) {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericValue);
}

export function QuantityStepper({ value, onChange, label, disabled = false, compact = false }) {
  return (
    <div className={`quantity-stepper ${compact ? 'quantity-stepper-compact' : ''}`} aria-label={label}>
      <button type='button' onClick={() => onChange(Math.max(1, value - 1))} disabled={disabled || value <= 1} aria-label={`Decrease ${label}`}>
        <FiMinus aria-hidden='true' />
      </button>
      <span aria-live='polite'>{value}</span>
      <button type='button' onClick={() => onChange(Math.min(100, value + 1))} disabled={disabled || value >= 100} aria-label={`Increase ${label}`}>
        <FiPlus aria-hidden='true' />
      </button>
    </div>
  );
}

export function ProductCard({ product, onAdd, busy }) {
  const [selectedSizeId, setSelectedSizeId] = useState(() => product.sizes?.[0]?.id || '');
  const [quantity, setQuantity] = useState(1);
  const selectedSize = product.sizes?.find((size) => Number(size.id) === Number(selectedSizeId));
  const linePrice = Number(selectedSize?.price || 0) * quantity;

  return (
    <article className='store-product-card'>
      <img src={product.imageUrl} alt={`${product.productName} boiled peanuts`} loading='lazy' width='640' height='640' />
      <div className='store-product-copy'>
        <h3>{product.productName}</h3>
        <p>{product.description}</p>
      </div>
      {product.sizes?.length ? (
        <div className='store-product-controls'>
          <label>
            <span>Size</span>
            <select value={selectedSizeId} onChange={(event) => setSelectedSizeId(Number(event.target.value))}>
              {product.sizes.map((size) => (
                <option value={size.id} key={size.id}>{size.size} — {formatCurrency(size.price)}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Quantity</span>
            <QuantityStepper value={quantity} onChange={setQuantity} label={`${product.productName} quantity`} />
          </label>
        </div>
      ) : <p className='store-product-unavailable'>Temporarily unavailable</p>}
      <button
        className='store-add-button'
        type='button'
        disabled={busy || !selectedSize}
        onClick={() => onAdd({ ...product, selectedSizeId: Number(selectedSizeId), selectedQuantity: quantity })}
      >
        {busy ? 'Adding…' : selectedSize ? `Add to cart — ${formatCurrency(linePrice)}` : 'Unavailable'}
      </button>
    </article>
  );
}

export function CartLineItem({ item, onUpdate, onRemove, busy, compact = false }) {
  return (
    <article className={`store-cart-line ${compact ? 'store-cart-line-compact' : ''}`}>
      <img src={item.imageUrl} alt='' width='120' height='120' />
      <div className='store-cart-line-copy'>
        <h3>{item.productName}</h3>
        <p>{item.selectedSize}</p>
        <span>{formatCurrency(item.unitPrice)} each</span>
        <QuantityStepper
          compact
          value={Number(item.selectedQuantity)}
          onChange={(quantity) => onUpdate(item.id, quantity)}
          label={`${item.productName} quantity`}
          disabled={busy}
        />
      </div>
      <div className='store-cart-line-end'>
        <strong>{formatCurrency(item.lineTotal)}</strong>
        <button type='button' onClick={() => onRemove(item.id)} disabled={busy} aria-label={`Remove ${item.productName} from cart`}>
          <FiTrash2 aria-hidden='true' />
        </button>
      </div>
    </article>
  );
}

export function OrderSummary({ items, policy, children }) {
  const subtotal = items.reduce((total, item) => total + Number(item.lineTotal || 0), 0);
  return (
    <div className='store-order-summary'>
      <div className='store-summary-row'><span>Subtotal</span><strong>{formatCurrency(subtotal)}</strong></div>
      <div className='store-pickup-note'>
        <FiMapPin aria-hidden='true' />
        <div><strong>Local pickup only</strong><span>Please allow at least {policy?.pickupLeadDays || 21} days</span></div>
      </div>
      {children}
    </div>
  );
}

export function CartDrawer({ open, onClose, items, onUpdate, onRemove, busy, policy }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll('button:not(:disabled), a[href], select:not(:disabled), input:not(:disabled)')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.body.classList.add('store-drawer-open');
    document.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();
    return () => {
      document.body.classList.remove('store-drawer-open');
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className='store-cart-layer'>
      <button className='store-cart-scrim' type='button' onClick={onClose} aria-label='Close cart' />
      <aside className='store-cart-drawer' ref={panelRef} role='dialog' aria-modal='true' aria-labelledby='cart-drawer-title'>
        <div className='store-cart-drawer-handle' aria-hidden='true' />
        <header>
          <h2 id='cart-drawer-title'>Your cart ({items.length})</h2>
          <button type='button' ref={closeRef} onClick={onClose} aria-label='Close cart'><FiX /></button>
        </header>
        <div className='store-cart-drawer-items'>
          {items.length ? items.map((item) => (
            <CartLineItem item={item} onUpdate={onUpdate} onRemove={onRemove} busy={busy} compact key={item.id} />
          )) : (
            <div className='store-cart-empty'>
              <FiShoppingBag aria-hidden='true' />
              <h3>Your cart is ready for a favorite.</h3>
              <p>Choose a flavor and size to get started.</p>
            </div>
          )}
        </div>
        {items.length ? (
          <OrderSummary items={items} policy={policy}>
            <Link className='store-checkout-link' to='/cart' onClick={onClose}>View cart &amp; choose pickup</Link>
          </OrderSummary>
        ) : null}
      </aside>
    </div>
  );
}

export function PickupSteps() {
  const steps = [
    { icon: FiShoppingBag, title: 'Choose your favorites', copy: 'Pick your flavor and size, then add it to your cart.' },
    { icon: FiCalendar, title: 'Pick a date at checkout', copy: 'Select your preferred pickup date during checkout.' },
    { icon: FiMapPin, title: 'We’ll have it ready', copy: 'Your order will be freshly prepared for pickup.' },
  ];
  return (
    <section className='pickup-steps' aria-labelledby='pickup-steps-title'>
      <h2 id='pickup-steps-title'>How pickup works</h2>
      <div>
        {steps.map(({ icon: Icon, title, copy }, index) => (
          <article key={title}>
            <span className='pickup-step-icon'><Icon aria-hidden='true' /></span>
            <div><strong>{index + 1}. {title}</strong><p>{copy}</p></div>
          </article>
        ))}
      </div>
    </section>
  );
}
